/**
 * The action bar's one-click actions, on today's flows:
 * - Favorite: the per-user pins (`usePinMutations`, the tree's Add to
 *   favorites). Pins what is not yet pinned; when everything already is, it
 *   unpins them, like the tree's toggle.
 * - Download: one uploaded file (the resolver only enables it for uploads)
 *   saves through `triggerAttachmentDownload`; several save as one zip from
 *   `POST /attachments/archive`. One anchor click per file does not work:
 *   cross-origin, each click is a navigation that cancels the one before it,
 *   so only one file ever arrived. A failure toasts, naming what it can.
 */

import { useCallback } from 'react';
import type { WorkspaceItem } from '@bower/shared';

import { useAuth } from '../../../contexts/AuthContext';
import { usePinMutations, usePins } from '../../../hooks/data/usePins';
import { useAppToast } from '../../../hooks/ui/useAppToast';
import { useCurrentUserQuery } from '../../../hooks/user/useUserQuery';
import { ANALYTICS_EVENTS } from '../../../constants/analytics';
import { apiService } from '../../../services/api.service';
import {
  AttachmentArchiveError,
  downloadAttachmentArchive,
} from '../../../services/api/attachments.api';
import { safeAnalytics } from '../../../utils/analytics';
import { downloadUrl, triggerAttachmentDownload } from '../../../utils/attachmentDownload';
import { formatLocalTimestamp } from '../../../utils/timeUtils';

/** What Favorite reads of a target: a detail page passes only these. */
type PinTarget = Pick<WorkspaceItem, 'id' | 'kind'>;

export function useQuickActions() {
  const toast = useAppToast();
  const { user } = useAuth();
  const { data: currentUser } = useCurrentUserQuery(!!user);
  const userId = currentUser?.id ?? '';
  const { data: pins = [] } = usePins(userId);
  const { pin, unpin } = usePinMutations(userId);
  const pinMutate = pin.mutate;
  const unpinMutate = unpin.mutate;

  const favorite = useCallback(
    (items: ReadonlyArray<PinTarget>) => {
      const pinByEntity = new Map(pins.map((p) => [p.entityId, p.id]));
      const unpinned = items.filter((item) => !pinByEntity.has(item.id));
      if (unpinned.length === 0) {
        for (const item of items) {
          const pinId = pinByEntity.get(item.id);
          if (pinId) unpinMutate(pinId);
        }
        return;
      }
      for (const item of unpinned) {
        pinMutate({
          entityType: item.kind === 'folder' ? 'project' : 'artifact',
          entityId: item.id,
        });
      }
    },
    [pins, pinMutate, unpinMutate],
  );

  const download = useCallback(
    async (items: ReadonlyArray<WorkspaceItem>) => {
      if (items.length === 1) {
        const [item] = items;
        await triggerAttachmentDownload(item.id, item.name).catch(() =>
          toast({
            title: 'Download failed',
            description: `Could not download ${item.name}.`,
            status: 'error',
          }),
        );
        return;
      }
      // The save starts only once the whole zip has arrived, so say so.
      toast({ title: `Preparing ${items.length} files to download`, status: 'info' });
      try {
        const blob = await downloadAttachmentArchive(
          apiService,
          items.map((item) => item.id),
        );
        const url = URL.createObjectURL(blob);
        // Local day, not UTC, as the data export names its file.
        downloadUrl(url, `bower-files-${formatLocalTimestamp(new Date()).slice(0, 10)}.zip`);
        // Revoked after the click has handed the Blob to the download.
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        safeAnalytics.track(ANALYTICS_EVENTS.BULK_OPERATION_COMPLETED, {
          operation_type: 'download',
          entity_type: 'artifact',
          item_count: items.length,
          failed_count: 0,
          total_count: items.length,
        });
      } catch (error) {
        const code = error instanceof AttachmentArchiveError ? error.code : 'UNKNOWN';
        const missing = new Set(
          error instanceof AttachmentArchiveError ? error.unavailableIds : [],
        );
        const names = items.filter((item) => missing.has(item.id)).map((item) => item.name);
        let description = 'Could not download these files.';
        if (code === 'ARCHIVE_TOO_LARGE') {
          description = 'That is too much for one download. Choose up to 50 files, 2 GB in total.';
        } else if (names.length > 0) {
          description = `Could not find ${names.join(', ')}. Nothing was downloaded.`;
        }
        toast({
          title: 'Download failed',
          description,
          status: 'error',
        });
      }
    },
    [toast],
  );

  /** Every item is already a favorite, so Favorite would unpin them. */
  const isFavorite = useCallback(
    (items: ReadonlyArray<PinTarget>) => {
      const pinned = new Set(pins.map((p) => p.entityId));
      return items.length > 0 && items.every((item) => pinned.has(item.id));
    },
    [pins],
  );

  return { favorite, download, isFavorite };
}
